import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ImagePlus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { contentTypeOf, putBlob } from "@/lib/upload";
import type { ProductDetail } from "@elektr/shared";

/** Mahsulot rasmlari: yuklash (to'g'ridan-to'g'ri bucket'ga), tartib (birinchisi — muqova) va o'chirish. */
export function ImagesManager({ product }: { product: ProductDetail }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const images = product.images ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["product", product.id] });

  async function uploadOne(file: File) {
    const ctype = contentTypeOf(file);
    const plan = unwrap(
      await api.POST("/api/products/{id}/images/", {
        params: { path: { id: product.id } },
        body: { filename: file.name, content_type: ctype, size: file.size },
      }),
    );
    await putBlob(plan.upload.url, file, plan.upload.headers, () => undefined);
    unwrap(
      await api.POST("/api/product-images/{id}/complete/", { params: { path: { id: plan.id } } }),
    );
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy((n) => n + files.length);
    for (const f of Array.from(files)) {
      try {
        await uploadOne(f);
      } catch (e) {
        toast.error(`${f.name}: ${errorMessage(e, t("common.error"))}`);
      } finally {
        setBusy((n) => n - 1);
        void refresh();
      }
    }
    if (input.current) input.current.value = "";
  }

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE("/api/product-images/{id}/", { params: { path: { id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => void refresh(),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  // Tartibni o'zgartirish: qo'shni bilan almashamiz va hammasiga 0..n-1 beramiz
  const move = useMutation({
    mutationFn: async ({ from, to }: { from: number; to: number }) => {
      const order = images.map((i) => i.id);
      [order[from], order[to]] = [order[to]!, order[from]!];
      for (const [pos, id] of order.entries()) {
        unwrap(
          await api.PATCH("/api/product-images/{id}/", {
            params: { path: { id } },
            body: { order: pos },
          }),
        );
      }
    },
    onSuccess: () => void refresh(),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={input}
          type="file"
          hidden
          multiple
          accept="image/*,.heic,.heif"
          data-testid="image-input"
          onChange={(e) => void onFiles(e.target.files)}
        />
        <Button variant="outline" onClick={() => input.current?.click()} disabled={busy > 0}>
          <ImagePlus className="size-4" /> {t("catalog.add_photos")}
        </Button>
        {busy > 0 && <Spinner />}
        <span className="text-xs text-muted-foreground">{t("catalog.photos_hint")}</span>
      </div>
      {images.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label={t("catalog.gallery")}>
          {images.map((im, i) => (
            <li key={im.id} className="flex flex-col gap-1 rounded-lg border border-border p-1.5">
              <div className="relative aspect-square overflow-hidden rounded-md bg-muted">
                {im.thumb_url && (
                  <img src={im.thumb_url} alt="" className="size-full object-cover" />
                )}
                {i === 0 && (
                  <span className="absolute left-1 top-1 rounded bg-primary px-1.5 text-xs font-bold text-primary-foreground">
                    {t("catalog.cover")}
                  </span>
                )}
              </div>
              <div className="flex justify-between">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("catalog.move_left")}
                  disabled={i === 0 || move.isPending}
                  onClick={() => move.mutate({ from: i, to: i - 1 })}
                >
                  <ArrowLeft className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("catalog.move_right")}
                  disabled={i === images.length - 1 || move.isPending}
                  onClick={() => move.mutate({ from: i, to: i + 1 })}
                >
                  <ArrowRight className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t("common.delete")}
                  onClick={() => remove.mutate(im.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
